import { useEffect, useState } from "react";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";

/**
 * Gates every surface in `src/pages/ad-studio-demo` — see the README there for
 * the call sites and their production fallbacks. The flag defaults off: an
 * unknown flag reads as `undefined`, which keeps each call site on its
 * production behavior.
 */
export const AD_STUDIO_DEMO_FLAG = "appAdStudioDemo";

export const useIsAdStudioDemoEnabled = (): boolean => {
  const flags = useFlags();

  return !!flags[AD_STUDIO_DEMO_FLAG];
};

/**
 * Same flag, but `undefined` until LaunchDarkly has finished initializing.
 *
 * `FeatureFlagProvider` uses `deferInitialization` and `FeatureFlagLoader`
 * deliberately renders children before initialization completes, so on a cold
 * load there is a window where `useFlags()` is `{}` and every flag reads as
 * off. `useIsAdStudioDemoEnabled` collapses that window to `false`, which is
 * the right default for anything that *renders* — you get at worst a brief
 * flash of the production surface.
 *
 * It is the wrong default for anything that *navigates*: a redirect fires from
 * an effect and unmounts the component that would have re-evaluated the flag,
 * so a flag-on user is sent to the production route permanently. Those callers
 * must wait for `undefined` to resolve before acting.
 */
export const useAdStudioDemoFlagWhenReady = (): boolean | undefined => {
  const ldClient = useLDClient();
  const isEnabled = useIsAdStudioDemoEnabled();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!ldClient) return;

    let isCanceled = false;
    // Resolve on failure too — a five-second timeout or a network error should
    // fall through to the production route rather than hang the redirect.
    ldClient
      .waitForInitialization(5)
      .catch(() => undefined)
      .finally(() => {
        if (!isCanceled) setIsReady(true);
      });

    return () => {
      isCanceled = true;
    };
  }, [ldClient]);

  return isReady ? isEnabled : undefined;
};
